import { useEffect, useMemo, useState, type FormEvent } from "react";
import { Ban, CheckCircle2, Play, Plus, RefreshCw, XCircle } from "lucide-react";
import { ErrorBanner } from "@/components/board/ErrorBanner";
import { LoadingState } from "@/components/board/LoadingState";
import { CopyButton } from "@/components/clients/CopyButton";
import { Modal } from "@/components/Modal";
import { useToast } from "@/components/Toast";
import { apiKeys, type ApiKeyMetadata, type CreatedApiKey } from "@/lib/apiKeys";

function formatDateTime(value: string | null): string {
  if (!value) return "Never";
  return new Intl.DateTimeFormat(undefined, {
    month: "short",
    day: "numeric",
    year: "numeric",
    hour: "numeric",
    minute: "2-digit",
  }).format(new Date(value));
}

function getStatus(key: ApiKeyMetadata): { label: string; tone: "active" | "expired" | "revoked" } {
  if (key.revoked_at || !key.is_active) return { label: "Revoked", tone: "revoked" };
  if (new Date(key.expires_at) <= new Date()) return { label: "Expired", tone: "expired" };
  return { label: "Active", tone: "active" };
}

function shellQuote(value: string): string {
  return `'${value.replace(/'/g, "'\\''")}'`;
}

export function ApiKeysSettings() {
  const { addToast } = useToast();
  const [keys, setKeys] = useState<ApiKeyMetadata[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [name, setName] = useState("");
  const [createModalOpen, setCreateModalOpen] = useState(false);
  const [createdKey, setCreatedKey] = useState<CreatedApiKey | null>(null);
  const [creating, setCreating] = useState(false);
  const [revokingId, setRevokingId] = useState<string | null>(null);
  const [refreshing, setRefreshing] = useState(false);
  const [testingKey, setTestingKey] = useState(false);
  const [testResult, setTestResult] = useState<{ tone: "success" | "error"; message: string } | null>(null);

  const trimmedName = name.trim();
  const canCreate = trimmedName.length > 0 && trimmedName.length <= 80 && !creating;

  const sortedKeys = useMemo(
    () => [...keys].sort((a, b) => new Date(b.created_at).getTime() - new Date(a.created_at).getTime()),
    [keys],
  );
  const createdKeyCurlCommand = useMemo(() => {
    if (!createdKey) return "";
    return [
      "curl",
      "-i",
      "-X",
      "GET",
      shellQuote(apiKeys.getMeUrl()),
      "-H",
      shellQuote(`Authorization: Bearer ${createdKey.api_key}`),
    ].join(" ");
  }, [createdKey]);

  const createdKeyMcpCommand = useMemo(() => {
    if (!createdKey) return "";
    return [
      "claude",
      "mcp",
      "add",
      "--transport",
      "http",
      "capacitor",
      apiKeys.getMcpUrl(),
      "--header",
      shellQuote(`Authorization: Bearer ${createdKey.api_key}`),
    ].join(" ");
  }, [createdKey]);

  const createdKeyMcpJson = useMemo(() => {
    if (!createdKey) return "";
    return JSON.stringify(
      {
        mcpServers: {
          capacitor: {
            type: "http",
            url: apiKeys.getMcpUrl(),
            headers: { Authorization: `Bearer ${createdKey.api_key}` },
          },
        },
      },
      null,
      2,
    );
  }, [createdKey]);

  // OAuth users need no pre-shared key — the same command without a header
  // triggers the browser sign-in flow.
  const oauthMcpCommand = useMemo(
    () => `claude mcp add --transport http capacitor ${apiKeys.getMcpUrl()}`,
    [],
  );

  const loadKeys = async (isRefresh = false) => {
    if (isRefresh) setRefreshing(true);
    setError(null);
    try {
      const data = await apiKeys.list();
      setKeys(data);
    } catch (err) {
      setError(err instanceof Error ? err.message : "Failed to load API keys.");
    } finally {
      setLoading(false);
      setRefreshing(false);
    }
  };

  useEffect(() => {
    loadKeys();
  }, []);

  const closeCreateModal = () => {
    if (creating) return;
    setCreateModalOpen(false);
    setName("");
  };

  const handleCreate = async (event: FormEvent) => {
    event.preventDefault();
    if (!canCreate) return;

    setCreating(true);
    setError(null);
    try {
      const created = await apiKeys.create(trimmedName);
      setCreatedKey(created);
      setTestResult(null);
      setName("");
      setCreateModalOpen(false);
      await loadKeys();
      addToast("API key created.", "success");
    } catch (err) {
      addToast(err instanceof Error ? err.message : "Failed to create API key.", "error");
    } finally {
      setCreating(false);
    }
  };

  const handleTestCreatedKey = async () => {
    if (!createdKey || testingKey) return;

    setTestingKey(true);
    setTestResult(null);
    try {
      const me = await apiKeys.testMe(createdKey.api_key);
      setTestResult({
        tone: "success",
        message: `Works. /me returned ${me.name || me.email || "your user"}.`,
      });
    } catch (err) {
      setTestResult({
        tone: "error",
        message: err instanceof Error ? err.message : "The /me test failed.",
      });
    } finally {
      setTestingKey(false);
    }
  };

  const handleRevoke = async (id: string) => {
    setRevokingId(id);
    try {
      const revoked = await apiKeys.revoke(id);
      setKeys((current) =>
        current.map((key) =>
          key.id === id
            ? {
                ...key,
                is_active: revoked.is_active,
                revoked_at: revoked.revoked_at,
                updated_at: new Date().toISOString(),
              }
            : key,
        ),
      );
      addToast("API key revoked.", "success");
    } catch (err) {
      addToast(err instanceof Error ? err.message : "Failed to revoke API key.", "error");
    } finally {
      setRevokingId(null);
    }
  };

  if (loading) {
    return <LoadingState message="Loading API keys..." />;
  }

  return (
    <div className="app-settings api-keys-settings">
      {error && <ErrorBanner message={error} entityName="API keys" />}

      <section className="settings-section api-keys-settings__section">
        <div className="settings-section__header">
          <h2 className="settings-section__title">API Keys</h2>
          <div className="settings-section__actions">
            <button type="button" className="api-keys-settings__create-button" onClick={() => setCreateModalOpen(true)}>
              <Plus size={14} aria-hidden="true" />
              Create key
            </button>
            <button
              type="button"
              className="api-keys-settings__refresh-button"
              onClick={() => loadKeys(true)}
              disabled={refreshing}
              title="Refresh API keys"
              aria-label="Refresh API keys"
            >
              <RefreshCw size={14} aria-hidden="true" />
            </button>
          </div>
        </div>

        {createModalOpen && (
          <Modal title="Create API Key" onClose={closeCreateModal} size="small">
            <form className="modal-form api-keys-settings__create-form" onSubmit={handleCreate}>
              <div className="form-field">
                <label className="form-label" htmlFor="api-key-name">
                  Key name
                </label>
                <input
                  id="api-key-name"
                  type="text"
                  value={name}
                  maxLength={80}
                  onChange={(event) => setName(event.target.value)}
                  placeholder="e.g. Viviana's Redash Slackbot Key"
                  className="form-input api-keys-settings__modal-input"
                  autoFocus
                />
              </div>
              <div className="modal-actions">
                <button
                  type="button"
                  className="api-keys-settings__cancel-button"
                  onClick={closeCreateModal}
                  disabled={creating}
                >
                  Cancel
                </button>
                <button type="submit" className="api-keys-settings__create-button" disabled={!canCreate}>
                  {creating ? "Creating..." : "Create API key"}
                </button>
              </div>
            </form>
          </Modal>
        )}

        {createdKey && (
          <div className="api-keys-settings__created" role="status" aria-live="polite">
            <div className="api-keys-settings__created-header">
              <div>
                <span className="api-keys-settings__created-kicker">New API key created</span>
                <h3 className="api-keys-settings__created-title">{createdKey.name}</h3>
              </div>
              <button
                type="button"
                className="api-keys-settings__dismiss-button"
                onClick={() => {
                  setCreatedKey(null);
                  setTestResult(null);
                }}
              >
                Done
              </button>
            </div>

            <p className="api-keys-settings__created-warning">
              Save this key now. Once this message disappears, Capacitor cannot show the full API key again.
            </p>

            <div className="api-keys-settings__copy-row">
              <div className="api-keys-settings__copy-content">
                <span className="api-keys-settings__copy-label">API key</span>
                <code className="api-keys-settings__created-key">{createdKey.api_key}</code>
              </div>
              <CopyButton value={createdKey.api_key} label="Copy API key" />
            </div>

            <div className="api-keys-settings__test-panel">
              <div className="api-keys-settings__test-header">
                <div>
                  <span className="api-keys-settings__copy-label">Quick test</span>
                  <p className="api-keys-settings__test-copy">
                    Call <code>/me</code> with this key to confirm it is active.
                  </p>
                </div>
                <button
                  type="button"
                  className="api-keys-settings__test-button"
                  onClick={handleTestCreatedKey}
                  disabled={testingKey}
                >
                  <Play size={14} aria-hidden="true" />
                  {testingKey ? "Testing..." : "Test /me"}
                </button>
              </div>

              {testResult && (
                <div className={`api-keys-settings__test-result api-keys-settings__test-result--${testResult.tone}`}>
                  {testResult.tone === "success" ? (
                    <CheckCircle2 size={14} aria-hidden="true" />
                  ) : (
                    <XCircle size={14} aria-hidden="true" />
                  )}
                  <span>{testResult.message}</span>
                </div>
              )}

              <div className="api-keys-settings__copy-row api-keys-settings__copy-row--curl">
                <div className="api-keys-settings__copy-content">
                  <span className="api-keys-settings__copy-label">Curl command</span>
                  <code className="api-keys-settings__curl-command">{createdKeyCurlCommand}</code>
                </div>
                <CopyButton value={createdKeyCurlCommand} label="Copy curl command" />
              </div>
            </div>

            <div className="api-keys-settings__mcp-panel">
              <div className="api-keys-settings__mcp-header">
                <span className="api-keys-settings__copy-label">Connect an AI agent (MCP)</span>
                <p className="api-keys-settings__test-copy">
                  Capacitor hosts a remote MCP server so coding agents (Claude Code, Cursor, Codex…) can query
                  this data. Add it with this key, or see the{" "}
                  <a
                    className="api-keys-settings__mcp-link"
                    href={apiKeys.getLlmsTxtUrl()}
                    target="_blank"
                    rel="noreferrer"
                  >
                    llms.txt
                  </a>{" "}
                  reference.
                </p>
              </div>

              <div className="api-keys-settings__copy-row api-keys-settings__copy-row--curl">
                <div className="api-keys-settings__copy-content">
                  <span className="api-keys-settings__copy-label">Claude Code</span>
                  <code className="api-keys-settings__curl-command">{createdKeyMcpCommand}</code>
                </div>
                <CopyButton value={createdKeyMcpCommand} label="Copy Claude Code MCP command" />
              </div>

              <div className="api-keys-settings__copy-row api-keys-settings__copy-row--curl">
                <div className="api-keys-settings__copy-content">
                  <span className="api-keys-settings__copy-label">Generic MCP config (mcpServers)</span>
                  <pre className="api-keys-settings__mcp-json">{createdKeyMcpJson}</pre>
                </div>
                <CopyButton value={createdKeyMcpJson} label="Copy MCP JSON config" />
              </div>

              <p className="api-keys-settings__test-copy">
                Prefer OAuth? No key needed — run the command below and sign in through your browser:
              </p>
              <div className="api-keys-settings__copy-row api-keys-settings__copy-row--curl">
                <div className="api-keys-settings__copy-content">
                  <span className="api-keys-settings__copy-label">Claude Code (OAuth, no key)</span>
                  <code className="api-keys-settings__curl-command">{oauthMcpCommand}</code>
                </div>
                <CopyButton value={oauthMcpCommand} label="Copy OAuth MCP command" />
              </div>
            </div>
          </div>
        )}

        <div className="settings-table-wrapper api-keys-settings__table-wrapper">
          <table className="settings-table">
            <thead>
              <tr>
                <th className="settings-table__th">Name</th>
                <th className="settings-table__th">Prefix</th>
                <th className="settings-table__th">Expires</th>
                <th className="settings-table__th">Last Used</th>
                <th className="settings-table__th">Status</th>
                <th className="settings-table__th">Created</th>
                <th className="settings-table__th">Actions</th>
              </tr>
            </thead>
            <tbody>
              {sortedKeys.length === 0 ? (
                <tr>
                  <td className="settings-table__empty" colSpan={7}>
                    No API keys yet.
                  </td>
                </tr>
              ) : (
                sortedKeys.map((key) => {
                  const status = getStatus(key);
                  const canRevoke = status.tone === "active";
                  return (
                    <tr key={key.id} className="settings-table__row">
                      <td className="settings-table__cell">{key.name}</td>
                      <td className="settings-table__cell">
                        <code className="api-keys-settings__prefix">{key.key_prefix}</code>
                      </td>
                      <td className="settings-table__cell">{formatDateTime(key.expires_at)}</td>
                      <td className="settings-table__cell">{formatDateTime(key.last_used_at)}</td>
                      <td className="settings-table__cell">
                        <span className={`api-keys-settings__status api-keys-settings__status--${status.tone}`}>
                          {status.label}
                        </span>
                      </td>
                      <td className="settings-table__cell">{formatDateTime(key.created_at)}</td>
                      <td className="settings-table__cell">
                        <button
                          type="button"
                          className="api-keys-settings__revoke-button"
                          onClick={() => handleRevoke(key.id)}
                          disabled={!canRevoke || revokingId === key.id}
                          title="Revoke API key"
                        >
                          <Ban size={14} aria-hidden="true" />
                          Revoke
                        </button>
                      </td>
                    </tr>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </section>
    </div>
  );
}
